import { normalizeRegionSearchText } from './normalizeRegionSearchText';

/**
 * Countries a Saudi lane can end in, by the `customCode` of their
 * `country` Region. A trip to one of them is filed to the Saudi border
 * crossing that serves it — the crossings carry the same code on
 * `hub.connectsToCountryCustomCode`.
 */
export type ForeignCountryCode =
  | 'qatar'
  | 'united_arab_emirates'
  | 'kuwait'
  | 'bahrain'
  | 'jordan'
  | 'iraq'
  | 'yemen'
  | 'oman';

export interface ForeignCountry {
  code: ForeignCountryCode;
  iso: string;
  /** Display name per language code. */
  names: Record<string, string>;
  /** Every spelling that names the country and nothing else, any language. */
  keywords: string[];
  /**\
   * `customCode`s of the Saudi crossings a truck to this country files
   * to. Code, not data: the importer rewrites `hub.*` and `keywords` from
   * the Excel on every run, and this list must survive that. Only
   * crossings Bayan can file are listed — الرقعي (Kuwait) has no Region
   * yet, so Kuwait resolves to الخفجي alone.
   */
  crossings: string[];
}

export const FOREIGN_COUNTRIES: Readonly<Record<ForeignCountryCode, ForeignCountry>> =
  Object.freeze({
    qatar: {
      code: 'qatar',
      iso: 'QA',
      names: { ar: 'قطر', en: 'Qatar', ur: 'قطر' },
      keywords: ['قطر', 'دولة قطر', 'qatar', 'katar'],
      crossings: ['salwa_border_crossing_(qatar)'],
    },
    united_arab_emirates: {
      code: 'united_arab_emirates',
      iso: 'AE',
      names: { ar: 'الإمارات', en: 'UAE', ur: 'متحدہ عرب امارات' },
      keywords: [
        'الإمارات',
        'الامارات',
        'امارات',
        'الإمارات العربية المتحدة',
        'متحدہ عرب امارات',
        'uae',
        'emirates',
        'united arab emirates',
      ],
      crossings: ['al_batha_border_crossing'],
    },
    kuwait: {
      code: 'kuwait',
      iso: 'KW',
      names: { ar: 'الكويت', en: 'Kuwait', ur: 'کویت' },
      keywords: ['الكويت', 'كويت', 'کویت', 'kuwait', 'kuweit'],
      crossings: ['al_durrah_/_khafji_crossing_(kuwait)'],
    },
    bahrain: {
      code: 'bahrain',
      iso: 'BH',
      names: { ar: 'البحرين', en: 'Bahrain', ur: 'بحرین' },
      keywords: ['البحرين', 'بحرين', 'بحرین', 'bahrain', 'bahrein'],
      crossings: ['king_fahd_causeway_(bahrain)'],
    },
    jordan: {
      code: 'jordan',
      iso: 'JO',
      names: { ar: 'الأردن', en: 'Jordan', ur: 'اردن' },
      keywords: ['الأردن', 'الاردن', 'اردن', 'jordan', 'amman'],
      crossings: [
        'al_haditha_border_crossing',
        'halat_ammar_border_crossing',
        'turaif_border_crossing_(jordan)',
      ],
    },
    iraq: {
      code: 'iraq',
      iso: 'IQ',
      names: { ar: 'العراق', en: 'Iraq', ur: 'عراق' },
      keywords: ['العراق', 'عراق', 'iraq', 'irak'],
      crossings: ['al_haditha_/_arar_border_crossing'],
    },
    yemen: {
      code: 'yemen',
      iso: 'YE',
      names: { ar: 'اليمن', en: 'Yemen', ur: 'یمن' },
      keywords: ['اليمن', 'يمن', 'یمن', 'yemen'],
      crossings: [
        'najran_/_al_wadiah_border_crossing_(yemen)',
        'al_tuwal_border_crossing_(yemen)',
        'al_khadra_border_crossing_(yemen)',
      ],
    },
    oman: {
      code: 'oman',
      iso: 'OM',
      names: { ar: 'عُمان', en: 'Oman', ur: 'عمان' },
      keywords: ['سلطنة عمان', 'oman', 'sultanate of oman'],
      crossings: ['rub_al_khali_border_crossing'],
    },
  });

/**
 * «عمان» is both عُمان (Oman) and عمّان (Amman, Jordan) — in Arabic and in
 * Urdu, and without diacritics nobody writes the difference. It always
 * asks; English spells the two apart and does not.
 */
export const AMBIGUOUS_COUNTRY_WORDS: Readonly<Record<string, readonly ForeignCountryCode[]>> =
  Object.freeze({ عمان: ['oman', 'jordan'] });

const TASHKEEL = /[\u064B-\u0652\u0670]/g;

/**
 * Folded for an exact comparison: tashkeel off, Urdu letter forms onto
 * Arabic ones, a leading «ال» / "the" off, punctuation and spacing
 * collapsed. Deliberately no fuzzing — «قطر» must never meet «القطيف».
 */
export const foldCountryText = (text: string): string =>
  normalizeRegionSearchText(text)
    .replace(TASHKEEL, '')
    .replace(/ک/g, 'ك')
    .replace(/[یى]/g, 'ي')
    .replace(/ہ/g, 'ه')
    .replace(/[.,،؛!?؟()"'«»]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^(ال|the )/, '');

const KEYWORD_INDEX: ReadonlyMap<string, ForeignCountryCode> = new Map(
  Object.values(FOREIGN_COUNTRIES).flatMap(country =>
    country.keywords.map(keyword => [foldCountryText(keyword), country.code] as const),
  ),
);

const AMBIGUOUS_INDEX: ReadonlyMap<string, readonly ForeignCountryCode[]> = new Map(
  Object.entries(AMBIGUOUS_COUNTRY_WORDS).map(([word, codes]) => [foldCountryText(word), codes]),
);

export type ForeignCountryMatch =
  | { kind: 'country'; code: ForeignCountryCode }
  | { kind: 'ambiguous'; codes: readonly ForeignCountryCode[] };

/**
 * The country a lane end names, when the whole text is a country name.
 * Exact only: «جدة» or «القطيف» or «قطر الندى» are not a country.
 */
export const matchForeignCountry = (text: string): ForeignCountryMatch | undefined => {
  const folded = foldCountryText(text);
  if (!folded) return undefined;
  const ambiguous = AMBIGUOUS_INDEX.get(folded);
  if (ambiguous) return { kind: 'ambiguous', codes: ambiguous };
  const code = KEYWORD_INDEX.get(folded);
  return code ? { kind: 'country', code } : undefined;
};

/**
 * Names people use for a crossing that its Region does not carry — often
 * the far side's name («العمري» is Jordan's post at الحديثة). Code, for
 * the same reason as `crossings`: the importer rewrites `aliases`.
 */
export const CROSSING_NICKNAMES: Readonly<Record<string, string>> = Object.freeze({
  العمري: 'al_haditha_border_crossing',
  'منفذ العمري': 'al_haditha_border_crossing',
  'al omari': 'al_haditha_border_crossing',
  الغويفات: 'al_batha_border_crossing',
  ghuwaifat: 'al_batha_border_crossing',
  'ابو سمرة': 'salwa_border_crossing_(qatar)',
  'abu samra': 'salwa_border_crossing_(qatar)',
  النويصيب: 'al_durrah_/_khafji_crossing_(kuwait)',
  'جسر الملك فهد': 'king_fahd_causeway_(bahrain)',
  'king fahd causeway': 'king_fahd_causeway_(bahrain)',
  'جديدة عرعر': 'al_haditha_/_arar_border_crossing',
});

const NICKNAME_INDEX: ReadonlyMap<string, string> = new Map(
  Object.entries(CROSSING_NICKNAMES).map(([name, code]) => [foldCountryText(name), code]),
);

/** The crossing `customCode` a nickname names, exact match only. */
export const crossingForNickname = (text: string): string | undefined =>
  NICKNAME_INDEX.get(foldCountryText(text));

/** The country a Saudi crossing leads to, by the crossing's `customCode`. */
export const countryOfCrossing = (customCode: string | undefined): ForeignCountryCode | undefined =>
  customCode
    ? Object.values(FOREIGN_COUNTRIES).find(country => country.crossings.includes(customCode))?.code
    : undefined;

/** Display name in `language`, else Arabic, else English. */
export const foreignCountryName = (code: ForeignCountryCode, language: string): string => {
  const names = FOREIGN_COUNTRIES[code].names;
  return names[language] ?? names['ar'] ?? names['en'];
};

/**
 * Saudi Arabia's bounding box, generous by a few kilometres. A geocode
 * fallback outside it is a different country's city of the same name
 * (or a foreign destination that must go through its crossing), never a
 * Saudi stop.
 */
export const SAUDI_BOUNDS = Object.freeze({ minLat: 16, maxLat: 32.3, minLng: 34.4, maxLng: 55.8 });

export const isInsideSaudiArabia = (point: { lat: number; lng: number }): boolean =>
  point.lat >= SAUDI_BOUNDS.minLat &&
  point.lat <= SAUDI_BOUNDS.maxLat &&
  point.lng >= SAUDI_BOUNDS.minLng &&
  point.lng <= SAUDI_BOUNDS.maxLng;
